// The keyboard inside the iOS app (@capacitor/keyboard, resize "none": mobile/capacitor.config.ts). The plugin stops the web view
// reacting to the keyboard, so iOS no longer slides the page up under it, and says how tall the keyboard will be as it starts to
// rise. The app then moves up with it (app/viewportHeight, styles/layout.css), rather than once the web view has caught up.

import { isNative, nativePlatform } from './native'

interface CapacitorGlobal {
  isPluginAvailable?: (name: string) => boolean
}

/** Capacitor copies the plugin's data onto a plain window event. */
type KeyboardEvent = Event & { keyboardHeight?: number }

/** Room left between a field and the keyboard's top edge. */
const ABOVE_KEYBOARD = 12

/** How long the keyboard has to come back once a panel of iOS's own closes: measured at under 100 ms. */
const KEYBOARD_BACK_WITHIN = 500

/**
 * Reports the keyboard's height as it starts to rise, and 0 as it starts to fall; 0 at once, so the visible window stops
 * deciding. Returns a cleanup, or null where the visible window still decides: a browser, Android (which resizes the web view
 * itself), and an app built before the plugin.
 */
export function startNativeKeyboard(report: (height: number) => void): (() => void) | null {
  if (!isNative() || nativePlatform() !== 'ios') return null
  const capacitor = (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor
  if (capacitor?.isPluginAvailable?.('Keyboard') !== true) return null
  // iOS takes the keyboard while one of its own panels is up (the file menu, the photo picker, a permission prompt): the web
  // view loses focus first, and the keyboard comes back when the panel closes, the field still focused. The app keeps the
  // keyboard's room meanwhile rather than sliding down and back, which left the file menu where the paperclip had been.
  let away = false
  let held = false
  let giveBack: ReturnType<typeof setTimeout> | undefined
  const release = () => {
    clearTimeout(giveBack)
    if (held) report(0)
    held = false
  }
  const show = (event: Event) => {
    clearTimeout(giveBack)
    held = false
    const height = (event as KeyboardEvent).keyboardHeight ?? 0
    report(height)
    revealFocus(height)
  }
  const shown = (event: Event) => revealFocus((event as KeyboardEvent).keyboardHeight ?? 0)
  const hide = () => {
    if (away && isEditable(document.activeElement)) held = true
    else report(0)
  }
  const blur = () => {
    away = true
  }
  const focus = () => {
    away = false
    if (!held) return
    if (isEditable(document.activeElement)) giveBack = setTimeout(release, KEYBOARD_BACK_WITHIN)
    else release()
  }
  window.addEventListener('keyboardWillShow', show)
  window.addEventListener('keyboardDidShow', shown)
  window.addEventListener('keyboardWillHide', hide)
  window.addEventListener('blur', blur)
  window.addEventListener('focus', focus)
  report(0)
  return () => {
    clearTimeout(giveBack)
    window.removeEventListener('keyboardWillShow', show)
    window.removeEventListener('keyboardDidShow', shown)
    window.removeEventListener('keyboardWillHide', hide)
    window.removeEventListener('blur', blur)
    window.removeEventListener('focus', focus)
  }
}

/** A field the keyboard types into. */
function isEditable(el: Element | null): boolean {
  return el instanceof HTMLTextAreaElement || el instanceof HTMLInputElement || (el instanceof HTMLElement && el.isContentEditable)
}

/**
 * The web view no longer scrolls a field out from under the keyboard (the plugin took the keyboard from it), so the app does:
 * its scrolling area glides it up alongside the keyboard as it rises, and again once the keyboard is up for a field at the end
 * of its area, which cannot scroll that far until the area has got shorter. Only that area scrolls: scrollIntoView would also
 * scroll the app's own clipped containers. The message box is in no scrolling area: the app moving up carries it.
 */
function revealFocus(keyboardHeight: number): void {
  const field = document.activeElement
  if (!(field instanceof HTMLElement) || field === document.body) return
  const covered = field.getBoundingClientRect().bottom - (window.innerHeight - keyboardHeight - ABOVE_KEYBOARD)
  if (covered > 0) scrollingAreaOf(field)?.scrollBy({ top: covered, behavior: 'smooth' })
}

function scrollingAreaOf(field: HTMLElement): HTMLElement | null {
  for (let el = field.parentElement; el && el !== document.body; el = el.parentElement) {
    const { overflowY } = getComputedStyle(el)
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el
  }
  return null
}
